import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import Alert from 'react-bootstrap/Alert';
import { db } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';

const ADMIN_EMAIL = 'ccrane2@alumni.nd.edu';

/**
 * Admin-only list of every online game, newest-active first, each linking
 * to /dev/game-review/:gameId to step through its recorded moves (see
 * GameReviewDetail.js). Reads onlineGames/players directly -- the
 * onlineGames Firestore rule lets the site owner read/list every game, not
 * just ones they're a participant in.
 */
export default function GameReviewList() {
  const { currentUser, authLoaded } = useAuth();
  const [games, setGames] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser || currentUser.email !== ADMIN_EMAIL) return;
    Promise.all([getDocs(collection(db, 'onlineGames')), getDocs(collection(db, 'players'))])
      .then(([gamesSnap, playersSnap]) => {
        const nameById = Object.fromEntries(playersSnap.docs.map(d => [d.id, d.data().name]));
        const loaded = gamesSnap.docs.map(d => {
          const g = d.data();
          return {
            id: d.id,
            player1Name: nameById[g.player1Id] ?? g.player1Id,
            player2Name: nameById[g.player2Id] ?? g.player2Id,
            status: g.status,
            round: g.round,
            scores: g.scores,
            // Most-recently-active moment available: a finished game sorts
            // by when it finished, an in-progress one by when it started,
            // one created but never started by its creation time.
            sortMillis: g.finishedAt?.toMillis?.() ?? g.startedAt?.toMillis?.() ?? g.createdAt?.toMillis?.() ?? null,
          };
        });
        loaded.sort((a, b) => (b.sortMillis ?? 0) - (a.sortMillis ?? 0));
        setGames(loaded);
      })
      .catch(err => setError(err.message));
  }, [currentUser]);

  if (!authLoaded) return <p>Loading…</p>;
  if (currentUser?.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  return (
    <div className="panel w-100">
      <div className="mb-3">
        <Link to="/dev">← Dev</Link>
      </div>
      <h2 className="section-title mb-3">Game Review (dev only)</h2>
      <p className="text-muted">
        Every online game, most recently active first. Pick one to step through its recorded moves --
        useful for tracking down a bug reported after the fact.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {games === null && !error && <p>Loading games…</p>}

      {games && (
        <div>
          {games.length === 0 && <p>No online games yet.</p>}
          {games.map(g => (
            <Link
              key={g.id}
              to={`/dev/game-review/${g.id}`}
              className="recent-game-row"
              style={{ alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            >
              <span>
                {g.player1Name} vs {g.player2Name}
                <span className="text-muted" style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                  {g.status === 'completed' ? `Finished ${g.scores.player1}-${g.scores.player2}` : `Round ${g.round}`}
                  {g.sortMillis &&
                    ` · ${new Date(g.sortMillis).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}`}
                </span>
              </span>
              <span className="text-muted">Review →</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
